import { watch } from 'vue';
import { useStore } from 'vuex';
import { useAnalytics } from '@satellite/composables/useAnalytics';
import { useSelectedWarehouse } from '@/composables/useSelectedWarehouse';
import type { WarehouseDetails } from '@satellite/composables/useWarehousesPresenter';

interface HeliosAnalyticsUser {
  id?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  orgId?: string | null;
  orgName?: string | null;
}

interface HeliosAnalyticsWarehouse {
  id?: string | number | null;
  name?: string | null;
}

export function useHeliosAnalytics() {
  const analytics = useAnalytics();
  const store = useStore();
  const { selectedWarehouse } = useSelectedWarehouse();

  const registerOrgSuperProperty = (me: HeliosAnalyticsUser) => {
    analytics.registerSuper({
      org_id: me.orgId ?? '',
      org_name: me.orgName ?? ''
    });
  };

  const registerUserSuperProperty = (me: HeliosAnalyticsUser) => {
    analytics.setUserProperties({
      $name: `${me.firstName ?? ''} ${me.lastName ?? ''}`,
      $email: me.email ?? '',
      'Nova User ID': me.id ?? '',
      'User Role': me.role ?? '',
      'Org ID': me.orgId ?? ''
    });
  };

  const registerWarehouseSuperProperty = (warehouse?: HeliosAnalyticsWarehouse | null) => {
    analytics.registerSuper({
      warehouse_id: String(warehouse?.id ?? ''),
      warehouse_name: String(warehouse?.name ?? '')
    });
  };

  watch(
    () => store.state.Auth.me,
    currentUser => {
      if (currentUser) {
        /**
         * distinct_id stays the user's email (not user.id) to preserve continuity with the
         * retired legacy plugin's Mixpanel cohorts/history for existing Helios users.
         */
        analytics.identify(currentUser.email);
        registerOrgSuperProperty(currentUser);
        registerUserSuperProperty(currentUser);
      } else {
        // Fires with a null user on every page load too, before Auth/getMe resolves.
        // MixpanelPlugin.reset() no-ops until identify() has succeeded, so only a
        // real logout reaches the SDK. Token expiry resets explicitly in src/routes.js.
        analytics.reset();
      }
    },
    { immediate: true }
  );

  /**
   * `selectedWarehouse` type-checks as a nested Ref due to a pre-existing storeToRefs/useLocalStorageRef
   * typing quirk in useWarehouseStore; the runtime value is always a plain WarehouseDetails | null.
   */
  watch(
    () => selectedWarehouse.value as unknown as WarehouseDetails | null,
    (warehouse: WarehouseDetails | null) => registerWarehouseSuperProperty(warehouse),
    { immediate: true }
  );
}
